// The locations list, driven by `POST /v1/workspaces/{ws}/locations/search`.
//
// Three pieces of state feed one request: free text (local, because a
// keystroke per history entry is worse than a non-shareable search box),
// filters (in the URL, via kore's filter-url grammar), and the sort (local,
// for the same reason as the query — kore's OrderBy has no URL grammar and
// inventing one here would be a second, private encoding).
//
// The free-text input is kore's own: `SearchBar` renders it (and debounces
// it, 200ms) once both `query` and `onQueryChange` are wired below —
// omitting either was this page's old mistake, and the reason its
// `FILTERED BY` bar used to render squeezed beside a hand-rolled search
// box instead of as its own full-width row underneath. `query` here
// already reflects kore's debounce; there is no second one to add.
//
// The cursor lives in `useCursorStack`, whose header explains why it is not
// kore's `useKoreCursorPagination`.
//
// The URL dict, the query/sort/page-size state and the request predicate come
// from `usePageSearch`, shared with screens.index.tsx: two copies of that
// wiring is what let the two pages diverge — see that hook's header.

import { Button, PageHead, SearchBar, useKoreSearch } from "@pej/kore/components";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useLocationsSearch } from "@/api/search";
import type { LocationQueryField, LocationSortField } from "@/api/types";
import { useWorkspaceContext } from "@/contexts/workspace-context";
import {
    LOCATION_DEFAULT_ORDER,
    LOCATION_QUERY_FIELDS,
    LOCATION_SORT_FIELDS,
    LOCATIONS_PAGE_SIZE_OPTIONS,
    LOCATIONS_SEARCH_MANIFEST,
    type LocationSearchBarFilters,
    locationFiltersForWire,
} from "@/features/locations/locations-filters";
import { LocationsTable } from "@/features/locations/locations-table";
import { usePageSearch } from "@/features/search/page-search";
import { useCursorStack } from "@/features/search/use-cursor-stack";

export const Route = createFileRoute("/w/$workspaceId/locations/")({
    component: LocationsPage,
});

function LocationsPage() {
    const { workspaceId } = useWorkspaceContext();
    const navigate = useNavigate();
    const page = usePageSearch<LocationSortField, LocationQueryField>({
        sortFields: LOCATION_SORT_FIELDS,
        defaultOrder: LOCATION_DEFAULT_ORDER,
        queryFields: LOCATION_QUERY_FIELDS,
        pageSizeOptions: LOCATIONS_PAGE_SIZE_OPTIONS,
    });

    const search = useKoreSearch<LocationSearchBarFilters>({
        manifest: LOCATIONS_SEARCH_MANIFEST,
        router: { search: page.rawSearch, onChange: page.onSearchPatch },
        orderBy: page.orderBy,
        onOrderByChange: page.onOrderByChange,
        // Wiring both is what makes `SearchBar` render its own query input
        // as row one of its two-row layout — see the file header. The
        // manifest declares no `query.fields`, so there is no scope to
        // pick between and `searchBy` stays `null`.
        query: page.query,
        onQueryChange: page.setQuery,
    });

    // kore's filter object is wider than the wire type — the URL can name any
    // op, and the status chip carries raw strings. Translate before the
    // request goes together; see `locationFiltersForWire`'s doc.
    const predicate = page.predicate(locationFiltersForWire(search.rq?.filters));

    const cursor = useCursorStack({ request: predicate });
    const locationsQuery = useLocationsSearch(workspaceId, { ...predicate, pageToken: cursor.pageToken });
    const pager = cursor.pager(locationsQuery.data?.nextPageToken ?? null);

    return (
        <div className="flex h-full flex-col gap-6">
            {/* h-full, so the table below fills the viewport instead of
                growing the page: `main` has a definite height (flex-1 inside
                the h-screen, overflow-hidden shell), so an h-full child fits
                exactly and no outer scrollbar appears. The table takes the
                remaining height and scrolls its own rows — see the
                `min-h-0 flex-1` passed to it, and kore's DataTable root,
                which is flex-col precisely so a height-constrained container
                pins its pager to the bottom. Same shape as the vouchers
                admin's crud-page. */}
            <PageHead
                size="h1"
                title="Locations"
                description="The restaurant locations sending postings to Estimated Waiting Time."
                action={
                    <Button onClick={() => navigate({ to: "/w/$workspaceId/locations/new", params: { workspaceId } })}>
                        <Plus aria-hidden="true" />
                        Create location
                    </Button>
                }
            />
            {/* Sibling of the table, not nested inside it: kore's own table
                card has its own borders and padding, and stacking the search
                row inside would nest one bordered bar inside another and
                inherit padding meant for the card, not the bar. */}
            {/* compact, not kore's default: `size="comfortable"` gives a 36px
                field and 36px filter pills, where every other table on the
                platform runs the 28px pair. Taking the library default by
                omission is what made these toolbars roomier than the rest. */}
            <SearchBar {...search.barProps} size="compact" />
            <LocationsTable
                className="min-h-0 flex-1"
                workspaceId={workspaceId}
                locations={locationsQuery.data?.items ?? []}
                isLoading={locationsQuery.isLoading}
                isError={locationsQuery.isError}
                onRetry={() => void locationsQuery.refetch()}
                sort={search.tableSortProps.sort}
                onSortChange={search.tableSortProps.onSortChange}
                sortableColumnKeys={search.sortableColumnKeys}
                emptyBecauseFiltered={page.searchText !== "" || search.filters.length > 0}
                pagination={{
                    ...pager,
                    pageSize: page.pageSize,
                    totalCount: locationsQuery.data?.totalCount ?? 0,
                    pageSizeOptions: LOCATIONS_PAGE_SIZE_OPTIONS,
                    onPageSizeChange: page.setPageSize,
                    entityLabel: "location",
                    // `DataTablePagination.isFetching` is deliberately not set:
                    // kore declares the field but reads it in neither
                    // data-table/index.tsx nor footer-pager.tsx, so passing it
                    // would read as though it drove a spinner it does not.
                }}
            />
        </div>
    );
}
